import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Filter, Loader2, MapPin, Search, Sparkles } from 'lucide-react';
import api from '@/lib/axios';
import { useSeo } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import { AdSlot, AdInlineList } from '@/components/marketplace/ads/AdSlot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('data:') || src.startsWith('http')) return src;
  return src.startsWith('/api/') ? `${BACKEND_URL}${src}` : src;
};

const PRESETS = {
  parceiros: {
    grupo: '', titulo: 'Parceiros e serviços TVDE',
    intro: 'Stands, associações e profissionais que trabalham todos os dias com motoristas TVDE.',
    seo: 'Parceiros TVDE · stands, associações e profissionais',
  },
  stands: {
    grupo: 'stands', titulo: 'Stands automóveis para TVDE',
    intro: 'Stands com viaturas prontas para TVDE, retomas e financiamento.',
    seo: 'Stands automóveis para TVDE',
  },
  associacoes: {
    grupo: 'associacoes', titulo: 'Associações TVDE',
    intro: 'Associações que apoiam motoristas e frotas TVDE em Portugal.',
    seo: 'Associações TVDE em Portugal',
  },
  profissionais: {
    grupo: 'profissionais', titulo: 'Profissionais e serviços TVDE',
    intro: 'Advogados, contabilistas, seguros, oficinas, formação, pneus, reboque e mais.',
    seo: 'Profissionais e serviços para TVDE',
  },
};

const PartnerCard = ({ item }) => (
  <Link
    to={`/parceiros/${item.slug}`}
    className={`group flex gap-3 rounded-2xl border bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-lg ${
      item.destaque ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}
    data-testid={`partner-card-${item.id}`}
  >
    {item.logotipo ? (
      <img src={resolveImg(item.logotipo)} alt={item.nome} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
    ) : (
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100">
        <Building2 className="h-6 w-6 text-slate-400" />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-sm font-bold text-slate-900">{item.nome}</p>
        {item.destaque && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`partner-featured-${item.id}`}>
            <Sparkles className="h-3 w-3" /> Em destaque
          </span>
        )}
      </div>
      <p className="mt-0.5 text-xs font-semibold text-emerald-700">{item.tipo_label}</p>
      <p className="mt-1 line-clamp-2 text-xs text-slate-600">{item.descricao}</p>
      <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
        <MapPin className="h-3 w-3 text-emerald-500" /> {item.distrito}{item.concelho ? ` · ${item.concelho}` : ''}
        {item.atendimento === 'online' ? ' · online' : item.atendimento === 'ambos' ? ' · presencial e online' : ''}
      </p>
    </div>
  </Link>
);

export default function MarketplacePartnersPage({ preset = 'parceiros' }) {
  const cfg = PRESETS[preset] || PRESETS.parceiros;
  const [params, setParams] = useSearchParams();
  const [facetas, setFacetas] = useState({ tipos: [], distritos: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 12 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');

  const filters = useMemo(() => ({
    q: params.get('q') || '',
    tipo: params.get('tipo') || '',
    distrito: params.get('distrito') || '',
    atendimento: params.get('atendimento') || '',
    ordenar: params.get('ordenar') || '',
    page: Number(params.get('page') || 1),
  }), [params]);

  useSeo({
    title: `${cfg.seo} · Marketplace TVDE`,
    description: cfg.intro,
    path: `/${preset === 'parceiros' ? 'parceiros' : preset === 'associacoes' ? 'associacoes-tvde' : preset === 'profissionais' ? 'profissionais-tvde' : 'stands'}`,
  });

  useEffect(() => { api.get('/marketplace/parceiros/facetas').then((r) => setFacetas(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const search = new URLSearchParams();
      if (cfg.grupo) search.append('grupo', cfg.grupo);
      Object.entries(filters).forEach(([k, v]) => { if (v) search.append(k, v); });
      search.append('limit', '12');
      const res = await api.get(`/marketplace/parceiros?${search.toString()}`);
      setData(res.data);
    } catch (_) {
      setData({ items: [], total: 0, page: 1, limit: 12 });
    } finally { setLoading(false); }
  }, [cfg.grupo, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!('page' in patch)) next.delete('page');
    setParams(next);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.limit || 12)));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="text-sm font-black text-slate-900">Marketplace <span className="text-emerald-600">TVDE</span></Link>
          <nav className="flex flex-wrap gap-2 text-[11px] font-semibold">
            {[['/parceiros', 'Todos'], ['/stands', 'Stands'], ['/associacoes-tvde', 'Associações'], ['/profissionais-tvde', 'Profissionais']].map(([to, label]) => (
              <Link key={to} to={to} className="rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-emerald-300 hover:text-emerald-700" data-testid={`partners-nav-${label.toLowerCase()}`}>{label}</Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl" data-testid="partners-title">{cfg.titulo}</h1>
            <p className="mt-2 max-w-2xl text-base text-slate-600">{cfg.intro}</p>
          </div>
          <Link to="/adicionar-parceiro" className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700" data-testid="partners-add-cta">
            Adicionar o meu negócio ou entidade
          </Link>
        </div>

        <div className="mt-6 grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setFilter({ q }); }}
              className="w-full rounded-xl border border-slate-300 pl-8 pr-3 py-2 text-sm" placeholder="Nome ou serviço"
              data-testid="partners-search" />
          </div>
          <select value={filters.tipo} onChange={(e) => setFilter({ tipo: e.target.value })}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm" data-testid="partners-filter-type">
            <option value="">Todas as categorias</option>
            {(facetas.tipos || []).filter((t) => !cfg.grupo || (facetas.grupos?.[cfg.grupo] || []).includes(t.valor))
              .map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
          </select>
          <select value={filters.distrito} onChange={(e) => setFilter({ distrito: e.target.value })}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm" data-testid="partners-filter-district">
            <option value="">Todos os distritos</option>
            {(facetas.distritos || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.atendimento} onChange={(e) => setFilter({ atendimento: e.target.value })}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm" data-testid="partners-filter-service">
            <option value="">Presencial ou online</option>
            <option value="presencial">Presencial</option>
            <option value="online">Online</option>
            <option value="ambos">Presencial e online</option>
          </select>
        </div>

        <p className="mt-4 inline-flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500" data-testid="partners-count">
          <Filter className="h-3.5 w-3.5" /> {data.total} {data.total === 1 ? 'perfil' : 'perfis'}
          <select value={filters.ordenar} onChange={(e) => setFilter({ ordenar: e.target.value })}
            className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600" data-testid="partners-sort">
            <option value="">Destaques primeiro</option>
            <option value="recentes">Mais recentes</option>
            <option value="nome">Nome (A-Z)</option>
            <option value="distrito">Distrito</option>
          </select>
          {(!filters.ordenar || filters.ordenar === 'destaques') && (
            <span className="text-[11px] font-normal text-slate-400">Perfis patrocinados aparecem primeiro</span>
          )}
        </p>

        {loading ? (
          <p className="mt-8 flex items-center gap-2 text-sm text-slate-500" data-testid="partners-loading"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
        ) : data.items.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="partners-empty">
            <Building2 className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm font-semibold text-slate-700">Ainda não há perfis publicados para estes filtros</p>
            <p className="mt-1 text-xs text-slate-500">Seja o primeiro a aparecer aqui.</p>
            <Link to="/adicionar-parceiro" className="mt-4 inline-block rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white" data-testid="partners-empty-cta">Adicionar o meu negócio</Link>
          </div>
        ) : (
          <>
            <AdSlot posicao="partners_top" pagina="/parceiros" distrito={filters.distrito} />
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <AdInlineList posicao="partners_list_inline" pagina="/parceiros" distrito={filters.distrito}>
                {data.items.map((item) => <PartnerCard key={item.id} item={item} />)}
              </AdInlineList>
            </div>
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2" data-testid="partners-pagination">
                <button type="button" disabled={filters.page <= 1} onClick={() => setFilter({ page: String(filters.page - 1) })}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold disabled:opacity-40" data-testid="partners-prev">Anterior</button>
                <span className="text-xs text-slate-500">Página {filters.page} de {totalPages}</span>
                <button type="button" disabled={filters.page >= totalPages} onClick={() => setFilter({ page: String(filters.page + 1) })}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold disabled:opacity-40" data-testid="partners-next">Seguinte</button>
              </div>
            )}
          </>
        )}
      </main>
      <MarketplaceFooter />
    </div>
  );
}
